import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { Subscription, tap, catchError, finalize } from 'rxjs';
import { IMenuItemAction, Logger, ErrorOrError, handleApiErrors } from '../../../../../core';
import { UserContextService } from '../../../../auth-management/authentication/services/user-context.service';
import { UserManagementPermissions } from '../../../../common/permissions/user-management.permissions';
import { AccountUsersListApiResponse, AccountUserItem } from '../../dtos/responses/account-user-response';
import { AccountUserService } from '../../services/account-user.service';
import { AssignRevokeSpecializedRolesComponent } from '../../specialized-roles/components/assign-revoke-specialized-roles/assign-revoke-specialized-roles.component';
import { AccountUserChangePasswordComponent } from '../account-user-change-password';
import { CreateUpdateAccountUserComponent } from '../create-update-account-user';
import { SharedTableComponent, BaseComponent, ToastService } from '../../../../../../trash/shared/components';
import { SahredConstants } from '../../../../../../trash/shared/constants/Values';

@Component({
  selector: 'app-account-user-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedTableComponent,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputIconModule,
    IconFieldModule,
    FormsModule,
    InputTextModule,
    CheckboxModule,
    SkeletonModule,
    NgbDropdownModule,
    MenuModule
  ],
  templateUrl: './account-user-list.component.html',
  styleUrl: './account-user-list.component.scss',
  providers: [DialogService]
})
export class AccountUserListComponent extends BaseComponent<any> {
  private subscriptions: Subscription[] = [];

  AccountUsersRes: AccountUsersListApiResponse | null = null;
  totalItemCount: number = 0;
  isLoadingItems: boolean = false;
  isLoadingFilter: boolean = false;
  searchTerm: string | null = null;

  items = {
    headers: ['GENERAL.FIRST_NAME', 'GENERAL.LAST_NAME', 'GENERAL.EMAIL', 'USER_MANAGEMENT.SPECIALIZED_ROLES', 'USER_MANAGEMENT.SYSTEM_ROLES', 'GENERAL.ACTIONS'],
    data: [] as AccountUserItem[],
    dataKeys: ['firstName', 'lastName', 'email', 'specializedRoles', 'systemRoles', 'action']
  };

  viewItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsUsers.Read);
  addItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsUsers.Create);
  editItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsUsers.Update);
  changePassword: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsUsers.ChangePassword);
  AssignPermissions: boolean = true;
  RevokePermissions: boolean = true;

  MenuItemsActionsList: IMenuItemAction[] = [];

  constructor(
    private _accountUserService: AccountUserService,
    private toastService: ToastService,
    private dialog: DialogService,
    private translate: TranslateService
  ) {
    super();
  }

  override ngOnInit(): void {
    this.MenuItemsActionsList = [
      {
        label: this.translate.instant('ACTIONS.EDIT'),
        icon: 'pi pi-pen-to-square',
        command: (event: any) => {
          this.openAccountUserDialog(event, true);
        },
        isView: this.editItem
      },
      {
        label: this.translate.instant('ACTIONS.VIEW'),
        icon: 'pi pi-eye',
        command: (event: any) => {
          this.openAccountUserDialog(event, false);
        },
        isView: this.viewItem
      },
      {
        label: this.translate.instant('ACTIONS.ASSIGN_SPECIALIZED_ROLES'),
        icon: 'pi pi-plus',
        type: 'assign',
        command: (event: any) => {
          Logger.debug('event', event);
          this.assignSpecializedRoles(event);
        },
        isView: this.AssignPermissions
      },
      {
        label: this.translate.instant('ACTIONS.REVOKE_SPECIALIZED_ROLES'),
        icon: 'pi pi-replay',
        type: 'revoke',
        command: (event: any) => {
          Logger.debug('event', event);
          this.revokeSpecializedRoles(event);
        },
        isView: this.RevokePermissions
      },
      {
        label: this.translate.instant('ACTIONS.CHANGE_PASSWORD'),
        icon: 'pi pi-sync',
        command: (event: any) => {
          Logger.debug('event', event);
          this.changePassord(event);
        },
        isView: this.changePassword
      }
    ];

    this.list(true); // Call the list method as usual
  }

  // Start Get Listing Functions
  async list(filter?: boolean) {
    if (this.HandelRenderingDataMessage) {
      this.HandelRenderingDataMessage = 'GENERAL.LOADING';
    }

    filter ? (this.isLoadingFilter = true) : (this.isLoadingItems = true);
    let RolesSubscription: Subscription = this._accountUserService
      .getAll(this.request)
      .pipe(
        tap((res: AccountUsersListApiResponse) => this.processListResponse(res)),
        catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
        finalize(() => this.finalizeListRequest())
      )
      .subscribe();

    this.subscriptions.push(RolesSubscription);
  }
  private processListResponse(response: AccountUsersListApiResponse): void {
    if (response) {
      this.AccountUsersRes = response;
      this.totalItemCount = response.paginationMetadata.totalItemCount ?? 0;
      this.items.data = this.AccountUsersRes.items;
      if (this.AccountUsersRes.items.length == 0) {
        this.HandelRenderingDataMessage = 'GENERAL.NO_DATA_FOUND';
      }
      this.cdr.markForCheck();
    } else {
      this.HandelRenderingDataMessage = 'GENERAL.NO_DATA_FOUND';
    }
  }
  private finalizeListRequest(): void {
    this.isLoadingItems = false;
    this.isLoadingFilter = false;
  }
  //  End Get Listing Functions

  openAccountUserDialog(item?: AccountUserItem | null, visible?: boolean) {
    let headerTitle: string;
    if (visible == false) {
      headerTitle = this.translate.instant('USER_MANAGEMENT.ACCOUNT_USER_DETAILS');
    } else {
      headerTitle = `${item ? this.translate.instant('ACTIONS.EDIT') : this.translate.instant('GENERAL.CREATE')} ${this.translate.instant('USER_MANAGEMENT.ACCOUNT_USER')}`;
    }
    const ref: any = this.dialog.open(CreateUpdateAccountUserComponent, {
      data: {
        item: item,
        visible: visible
      },
      header: headerTitle,
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose.subscribe((isSuccess: boolean) => {
      if (isSuccess) {
        setTimeout(async () => {
          await this.list();
        }, 1000);
      }
    });
  }
  private assignSpecializedRoles(item: any): void {
    const ref = this.dialog.open(AssignRevokeSpecializedRolesComponent, {
      data: {
        item: item,
        type: 'assign',
        visible: true
      },
      header: this.translate.instant('ACTIONS.ASSIGN_SPECIALIZED_ROLES'),
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose?.subscribe((item?: any) => {
      if (item) {
        this.resetParams();
        this.list();
      }
    });
  }
  private revokeSpecializedRoles(item: any): void {
    const ref = this.dialog.open(AssignRevokeSpecializedRolesComponent, {
      data: {
        item: item,
        type: 'revoke',
        visible: true
      },
      header: this.translate.instant('ACTIONS.REVOKE_SPECIALIZED_ROLES'),
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose?.subscribe((item?: any) => {
      if (item) {
        this.resetParams();
        this.list();
      }
    });
  }
  // Change Password
  private changePassord(item: any): void {
    const ref = this.dialog.open(AccountUserChangePasswordComponent, {
      data: {
        item: item,
        type: 'changePassord',
        visible: true
      },
      header: this.translate.instant('ACTIONS.CHANGE_PASSWORD'),
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose?.subscribe((item?: any) => {
      if (item) {
        this.resetParams();
        this.list();
      }
    });
  }

  resetParams(): void {
    this.page = 1;
    this.request.page = SahredConstants.INITIAL_PAGE_INDEX;
    this.request.pageSize = SahredConstants.INITIAL_PAGE_SIZE;
    this.searchTerm = null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
